/**
 * RevisioneAnalisiDialog — revisione di ciò che l'AI ha capito dal racconto,
 * prima che venga salvato. Nessuna scrittura avviene senza passare da qui.
 *
 * È il punto in cui la tassonomia resta pulita, perché ogni tag PROPOSTO offre
 * tre uscite invece di una:
 *  - «usa quello esistente» quando il sistema riconosce un quasi-duplicato
 *    (e l'etichetta proposta viene registrata come ALIAS del tag esistente →
 *    quel modo di dirlo non genererà mai più una proposta);
 *  - «crea come tag nuovo» quando è davvero un concetto nuovo;
 *  - «scarta».
 *
 * Doc canonico: docs/01-ARCHITECTURE/RETE-CONTATTI-MODEL.md
 */

import React, { useMemo, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, FormControlLabel, MenuItem, Paper, Stack, Switch, TextField,
  Tooltip, Typography, alpha,
} from '@mui/material';
import { AutoAwesome, Close, Link as LinkIcon, NewLabel } from '@mui/icons-material';
import { tokens } from '../../theme';
import { TagPicker } from './TagPicker';
import { CANALE_LABELS, CANALE_ORDER, COLORE_FACCETTA_DEFAULT, oggiIso } from './labels';
import { risolviTag, trovaTagSimili } from '@shared/rete/reteTassonomia';
import { aggiungiAlias, saveTag } from '../../services/reteContattiService';
import type {
  AnalizzaNotaVocaleResponse, CanaleInterazione, FaccettaRete, PersonaRete,
  PersonaReteWriteInput, TagProposto, TagRete,
} from '../../types/reteContatti';

/** Esito della revisione: cosa salvare. Lo applica il chiamante. */
export interface EsitoRevisione {
  nota: string;
  trascrizioneGrezza: string;
  quando: string;
  canale: CanaleInterazione;
  daNotaVocale: boolean;
  /** Tag finali della persona (già uniti a quelli che aveva). */
  tagFinali: string[];
  /** Campi anagrafici da aggiornare (solo quelli accettati). */
  patchScheda: Partial<PersonaReteWriteInput>;
}

interface Props {
  open: boolean;
  persona: PersonaRete;
  analisi: AnalizzaNotaVocaleResponse;
  faccette: FaccettaRete[];
  tag: TagRete[];
  /** true se il racconto arrivava da un audio (etichetta l'interazione). */
  daNotaVocale: boolean;
  onClose: () => void;
  /** Conferma: il chiamante salva e ricarica. */
  onConferma: (esito: EsitoRevisione) => Promise<void>;
  /** I tag sono cambiati (creati/alias): il chiamante ricarica la tassonomia. */
  onTassonomiaCambiata: () => Promise<void>;
  showSnack: (msg: string, severity: 'success' | 'error') => void;
}

/** Stato di lavorazione di una singola proposta dell'AI. */
type StatoProposta =
  | { tipo: 'aperta' }
  | { tipo: 'creato'; tagId: string }
  | { tipo: 'ricondotto'; tagId: string }
  | { tipo: 'scartato' };

export const RevisioneAnalisiDialog: React.FC<Props> = ({
  open, persona, analisi, faccette, tag, daNotaVocale,
  onClose, onConferma, onTassonomiaCambiata, showSnack,
}) => {
  const [nota, setNota] = useState(analisi.nota_pulita);
  const [quando, setQuando] = useState(oggiIso());
  const [canale, setCanale] = useState<CanaleInterazione>(
    daNotaVocale ? 'videochiamata' : 'altro',
  );
  const [salvando, setSalvando] = useState(false);
  const [lavorando, setLavorando] = useState<string | null>(null);

  /** Tag applicati: quelli che la persona già aveva + quelli riconosciuti dall'AI. */
  const [tagSelezionati, setTagSelezionati] = useState<string[]>(() =>
    Array.from(new Set([...(persona.tag ?? []), ...analisi.tag_agganciati.map((t) => t.tag_id)])),
  );

  /** Esito di ogni proposta, per indice. */
  const [statiProposte, setStatiProposte] = useState<Record<number, StatoProposta>>({});

  /** Suggerimenti anagrafici accettati (di default sì, solo per i campi vuoti). */
  const suggerimenti = analisi.suggerimenti_scheda ?? {};
  const [accettati, setAccettati] = useState<Record<string, boolean>>(() => {
    const iniziale: Record<string, boolean> = {};
    for (const [campo, valore] of Object.entries(suggerimenti)) {
      if (!valore) continue;
      // Proposta accettata di default solo se il campo è ancora vuoto: non
      // sovrascriviamo a sorpresa quello che l'utente ha già scritto a mano.
      iniziale[campo] = !((persona as unknown as Record<string, unknown>)[campo] ?? '');
    }
    return iniziale;
  });

  const faccetteById = useMemo(() => new Map(faccette.map((f) => [f.id, f])), [faccette]);
  const tagById = useMemo(() => new Map(tag.map((t) => [t.id, t])), [tag]);

  /** Motivi per tag, per mostrare "perché" accanto ai chip riconosciuti. */
  const motiviPerTag = useMemo(() => {
    const m = new Map<string, string>();
    for (const a of analisi.tag_agganciati) if (a.motivo) m.set(a.tag_id, a.motivo);
    return m;
  }, [analisi.tag_agganciati]);

  // ── Azioni sulle proposte ────────────────────────────────────────────────

  /** Crea davvero il tag proposto (stato attivo) e lo applica alla persona. */
  const creaProposta = async (indice: number, proposta: TagProposto) => {
    setLavorando(`crea-${indice}`);
    try {
      const slug = await saveTag({
        etichetta: proposta.etichetta,
        faccetta: proposta.faccetta,
        stato: 'attivo',
        alias: [],
      });
      setTagSelezionati((prev) => (prev.includes(slug) ? prev : [...prev, slug]));
      setStatiProposte((prev) => ({ ...prev, [indice]: { tipo: 'creato', tagId: slug } }));
      await onTassonomiaCambiata();
      showSnack(`Tag "${proposta.etichetta}" creato`, 'success');
    } catch (e) {
      showSnack(e instanceof Error ? e.message : 'Creazione tag non riuscita', 'error');
    } finally {
      setLavorando(null);
    }
  };

  /**
   * Riconduce la proposta a un tag ESISTENTE: lo applica alla persona e registra
   * l'etichetta proposta come suo alias. È la mossa che insegna al sistema —
   * la prossima volta quel modo di dirlo verrà riconosciuto da solo.
   */
  const riconduciProposta = async (indice: number, proposta: TagProposto, esistente: TagRete) => {
    setLavorando(`riconduci-${indice}`);
    try {
      await aggiungiAlias(esistente, proposta.etichetta);
      setTagSelezionati((prev) =>
        prev.includes(esistente.id) ? prev : [...prev, esistente.id],
      );
      setStatiProposte((prev) => ({
        ...prev,
        [indice]: { tipo: 'ricondotto', tagId: esistente.id },
      }));
      await onTassonomiaCambiata();
      showSnack(`"${proposta.etichetta}" ora è un sinonimo di "${esistente.etichetta}"`, 'success');
    } catch (e) {
      showSnack(e instanceof Error ? e.message : 'Operazione non riuscita', 'error');
    } finally {
      setLavorando(null);
    }
  };

  const scartaProposta = (indice: number) => {
    setStatiProposte((prev) => ({ ...prev, [indice]: { tipo: 'scartato' } }));
  };

  // ── Conferma ─────────────────────────────────────────────────────────────

  const conferma = async () => {
    if (!nota.trim()) {
      showSnack('La nota non può essere vuota', 'error');
      return;
    }
    setSalvando(true);
    try {
      const patchScheda: Partial<PersonaReteWriteInput> = {};
      for (const [campo, valore] of Object.entries(suggerimenti)) {
        if (valore && accettati[campo]) {
          (patchScheda as Record<string, unknown>)[campo] = valore;
        }
      }
      await onConferma({
        nota: nota.trim(),
        trascrizioneGrezza: analisi.trascrizione_grezza ?? '',
        quando,
        canale,
        daNotaVocale,
        tagFinali: tagSelezionati,
        patchScheda,
      });
    } finally {
      setSalvando(false);
    }
  };

  const proposteAperte = analisi.tag_proposti.filter(
    (_, i) => (statiProposte[i]?.tipo ?? 'aperta') === 'aperta',
  ).length;

  return (
    <Dialog open={open} onClose={salvando ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontFamily: tokens.font.display, fontWeight: 600, pb: 1 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <AutoAwesome fontSize="small" sx={{ color: tokens.color.tierBusiness.main }} />
          <span>Cosa ho capito</span>
        </Stack>
        <Typography variant="body2" sx={{ color: tokens.color.ash, mt: 0.5, fontFamily: tokens.font.body }}>
          Controlla e correggi: niente viene salvato prima della tua conferma.
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={3}>
          {/* NOTA ---------------------------------------------------------- */}
          <Box>
            <SezioneTitolo testo="Nota" />
            <TextField
              value={nota}
              onChange={(e) => setNota(e.target.value)}
              multiline
              minRows={4}
              fullWidth
              size="small"
              placeholder="La nota in pulito"
            />
            {analisi.trascrizione_grezza && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="caption" sx={{ color: tokens.color.mist }}>
                  Trascrizione originale (conservata sempre, non modificabile):
                </Typography>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 1.25, mt: 0.5, maxHeight: 110, overflow: 'auto',
                    bgcolor: tokens.color.bone, borderColor: tokens.color.line,
                  }}
                >
                  <Typography variant="caption" sx={{ color: tokens.color.ash, fontStyle: 'italic' }}>
                    {analisi.trascrizione_grezza}
                  </Typography>
                </Paper>
              </Box>
            )}
          </Box>

          {/* QUANDO / CANALE ----------------------------------------------- */}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
            <TextField
              label="Quando" type="date" value={quando} size="small"
              onChange={(e) => setQuando(e.target.value)}
              InputLabelProps={{ shrink: true }} sx={{ minWidth: 180 }}
            />
            <TextField
              label="Canale" select value={canale} size="small"
              onChange={(e) => setCanale(e.target.value as CanaleInterazione)}
              sx={{ minWidth: 200 }}
            >
              {CANALE_ORDER.map((c) => (
                <MenuItem key={c} value={c}>{CANALE_LABELS[c]}</MenuItem>
              ))}
            </TextField>
          </Stack>

          {/* TAG PROPOSTI --------------------------------------------------- */}
          {analisi.tag_proposti.length > 0 && (
            <Box>
              <SezioneTitolo testo={`Tag nuovi proposti (${proposteAperte} da decidere)`} />
              <Stack spacing={1.25}>
                {analisi.tag_proposti.map((proposta, indice) => (
                  <RigaProposta
                    key={`${proposta.etichetta}-${indice}`}
                    proposta={proposta}
                    stato={statiProposte[indice] ?? { tipo: 'aperta' }}
                    faccettaEtichetta={faccetteById.get(proposta.faccetta)?.etichetta ?? proposta.faccetta}
                    colore={faccetteById.get(proposta.faccetta)?.colore || COLORE_FACCETTA_DEFAULT}
                    tagEsistenti={tag}
                    tagRisultante={
                      statiProposte[indice] && 'tagId' in statiProposte[indice]
                        ? tagById.get((statiProposte[indice] as { tagId: string }).tagId)?.etichetta
                        : undefined
                    }
                    lavorando={lavorando}
                    indice={indice}
                    onCrea={() => creaProposta(indice, proposta)}
                    onRiconduci={(esistente) => riconduciProposta(indice, proposta, esistente)}
                    onScarta={() => scartaProposta(indice)}
                  />
                ))}
              </Stack>
            </Box>
          )}

          {/* TAG APPLICATI -------------------------------------------------- */}
          <Box>
            <SezioneTitolo testo="Tag della scheda" />
            {motiviPerTag.size > 0 && (
              <Stack spacing={0.5} sx={{ mb: 1.5 }}>
                {Array.from(motiviPerTag.entries()).map(([tagId, motivo]) => (
                  <Typography key={tagId} variant="caption" sx={{ color: tokens.color.mist }}>
                    <Box component="span" sx={{ fontWeight: 600, color: tokens.color.ash }}>
                      {tagById.get(tagId)?.etichetta ?? tagId}
                    </Box>
                    {` — ${motivo}`}
                  </Typography>
                ))}
              </Stack>
            )}
            <TagPicker
              faccette={faccette}
              tag={tag}
              selezionati={tagSelezionati}
              onChange={setTagSelezionati}
            />
          </Box>

          {/* SUGGERIMENTI SCHEDA -------------------------------------------- */}
          {Object.values(suggerimenti).some(Boolean) && (
            <Box>
              <SezioneTitolo testo="Dati sentiti nel racconto" />
              <Stack spacing={0.5}>
                {Object.entries(suggerimenti).map(([campo, valore]) =>
                  valore ? (
                    <FormControlLabel
                      key={campo}
                      control={(
                        <Switch
                          size="small"
                          checked={!!accettati[campo]}
                          onChange={(e) =>
                            setAccettati((prev) => ({ ...prev, [campo]: e.target.checked }))}
                        />
                      )}
                      label={(
                        <Typography variant="body2" sx={{ color: tokens.color.ash }}>
                          <Box component="span" sx={{ color: tokens.color.mist }}>
                            {ETICHETTE_CAMPI[campo] ?? campo}:{' '}
                          </Box>
                          {valore}
                        </Typography>
                      )}
                    />
                  ) : null,
                )}
              </Stack>
            </Box>
          )}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={salvando} color="inherit">Annulla</Button>
        <Button
          onClick={conferma}
          variant="contained"
          disabled={salvando || !nota.trim()}
          startIcon={salvando ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {salvando ? 'Salvo…' : 'Salva nota e tag'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ============================================================================
// SOTTO-COMPONENTI
// ============================================================================

const ETICHETTE_CAMPI: Record<string, string> = {
  azienda_testo: 'azienda',
  ruolo_testo: 'ruolo',
  cosa_cerca: 'cosa cerca',
  cosa_potrebbe_nascere: 'cosa potrebbe nascere',
};

const SezioneTitolo: React.FC<{ testo: string }> = ({ testo }) => (
  <Typography
    sx={{
      fontSize: '0.62rem', fontWeight: 700, letterSpacing: '0.1em',
      textTransform: 'uppercase', color: tokens.color.mist, mb: 1,
    }}
  >
    {testo}
  </Typography>
);

interface RigaPropostaProps {
  proposta: TagProposto;
  stato: StatoProposta;
  faccettaEtichetta: string;
  colore: string;
  tagEsistenti: TagRete[];
  tagRisultante?: string;
  lavorando: string | null;
  indice: number;
  onCrea: () => void;
  onRiconduci: (esistente: TagRete) => void;
  onScarta: () => void;
}

/**
 * Una proposta dell'AI con le sue tre uscite. Se il modulo di similarità trova
 * candidati, la strada suggerita è SEMPRE «usa quello esistente»: è la scelta
 * che tiene bassa la tassonomia.
 */
const RigaProposta: React.FC<RigaPropostaProps> = ({
  proposta, stato, faccettaEtichetta, colore, tagEsistenti, tagRisultante,
  lavorando, indice, onCrea, onRiconduci, onScarta,
}) => {
  // Candidati simili tra i soli tag ATTIVI (proporre un tag già in coda non serve).
  const simili = useMemo(
    () => trovaTagSimili(proposta.etichetta, tagEsistenti.filter((t) => t.stato === 'attivo')),
    [proposta.etichetta, tagEsistenti],
  );
  // Se un tag corrisponde esattamente, la CF l'avrebbe già ricondotto: questo è
  // solo un secondo controllo lato UI (la tassonomia può essere cambiata intanto).
  const esatto = useMemo(
    () => risolviTag(proposta.etichetta, tagEsistenti),
    [proposta.etichetta, tagEsistenti],
  );

  const chiuso = stato.tipo !== 'aperta';

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 1.5,
        borderColor: chiuso ? tokens.color.line : alpha(colore, 0.35),
        bgcolor: chiuso ? tokens.color.bone : alpha(colore, 0.04),
        opacity: stato.tipo === 'scartato' ? 0.55 : 1,
      }}
    >
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap', gap: 0.5 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, color: tokens.color.graphite }}>
              {proposta.etichetta}
            </Typography>
            <Chip
              label={faccettaEtichetta}
              size="small"
              sx={{
                height: 20, fontSize: '0.65rem',
                bgcolor: alpha(colore, 0.12), color: colore,
                border: `1px solid ${alpha(colore, 0.3)}`,
              }}
            />
          </Stack>
          {proposta.motivo && (
            <Typography variant="caption" sx={{ color: tokens.color.mist, display: 'block', mt: 0.25 }}>
              {proposta.motivo}
            </Typography>
          )}

          {/* Esito, quando la proposta è già stata lavorata */}
          {stato.tipo === 'creato' && (
            <Typography variant="caption" sx={{ color: tokens.color.success, fontWeight: 600 }}>
              creato come tag nuovo
            </Typography>
          )}
          {stato.tipo === 'ricondotto' && (
            <Typography variant="caption" sx={{ color: tokens.color.success, fontWeight: 600 }}>
              ricondotto a «{tagRisultante}» — d'ora in poi lo riconoscerà da solo
            </Typography>
          )}
          {stato.tipo === 'scartato' && (
            <Typography variant="caption" sx={{ color: tokens.color.mist }}>scartato</Typography>
          )}
        </Box>

        {!chiuso && (
          <Stack direction="row" spacing={0.75} sx={{ flexShrink: 0, flexWrap: 'wrap', gap: 0.75 }}>
            {/* Se esiste già qualcosa di simile, questa è la strada giusta */}
            {(esatto ? [esatto] : simili.map((s) => s.tag)).slice(0, 2).map((candidato) => (
              <Tooltip
                key={candidato.id}
                title={`Applica «${candidato.etichetta}» e registra «${proposta.etichetta}» come suo sinonimo`}
              >
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<LinkIcon sx={{ fontSize: 16 }} />}
                  disabled={lavorando !== null}
                  onClick={() => onRiconduci(candidato)}
                  sx={{ textTransform: 'none', fontSize: '0.75rem' }}
                >
                  usa «{candidato.etichetta}»
                </Button>
              </Tooltip>
            ))}
            <Button
              size="small"
              variant={esatto || simili.length ? 'text' : 'contained'}
              startIcon={
                lavorando === `crea-${indice}`
                  ? <CircularProgress size={14} color="inherit" />
                  : <NewLabel sx={{ fontSize: 16 }} />
              }
              disabled={lavorando !== null}
              onClick={onCrea}
              sx={{ textTransform: 'none', fontSize: '0.75rem' }}
            >
              crea nuovo
            </Button>
            <Button
              size="small"
              color="inherit"
              startIcon={<Close sx={{ fontSize: 16 }} />}
              disabled={lavorando !== null}
              onClick={onScarta}
              sx={{ textTransform: 'none', fontSize: '0.75rem', color: tokens.color.mist }}
            >
              scarta
            </Button>
          </Stack>
        )}
      </Stack>

      {!chiuso && simili.length > 0 && !esatto && (
        <Alert severity="info" sx={{ mt: 1, py: 0, fontSize: '0.75rem' }}>
          Esiste già qualcosa di simile: meglio usare quello, così i tag non si moltiplicano.
        </Alert>
      )}
    </Paper>
  );
};
